import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, ArrowLeft, Calendar, UserPlus, Download } from 'lucide-react';
import {
  addToCalendar, addContact, downloadIcs, downloadVcf, ms,
} from '../services/calendarAdapters.js';

const FIELD_SCHEMAS = {
  appointment: [
    { key: 'title',    label: 'Title',     type: 'text' },
    { key: 'provider', label: 'Provider',  type: 'text' },
    { key: 'location', label: 'Location',  type: 'text' },
    { key: 'datetime', label: 'Date & time', type: 'datetime-local' },
    { key: 'duration_minutes', label: 'Duration (min)', type: 'number' },
    { key: 'notes',    label: 'Notes',     type: 'textarea' },
  ],
  bill: [
    { key: 'payee',          label: 'Payee',          type: 'text' },
    { key: 'amount',         label: 'Amount',         type: 'number' },
    { key: 'due_date',       label: 'Due date',       type: 'date' },
    { key: 'account_number', label: 'Account number', type: 'text' },
    { key: 'notes',          label: 'Notes',          type: 'textarea' },
  ],
  flyer: [
    { key: 'title',       label: 'Title',       type: 'text' },
    { key: 'location',    label: 'Location',    type: 'text' },
    { key: 'datetime',    label: 'Date & time', type: 'datetime-local' },
    { key: 'description', label: 'Description', type: 'textarea' },
  ],
  businesscard: [
    { key: 'given_name',     label: 'First name',  type: 'text' },
    { key: 'surname',        label: 'Last name',   type: 'text' },
    { key: 'job_title',      label: 'Job title',   type: 'text' },
    { key: 'company',        label: 'Company',     type: 'text' },
    { key: 'email',          label: 'Email',       type: 'email' },
    { key: 'mobile_phone',   label: 'Mobile',      type: 'tel' },
    { key: 'business_phone', label: 'Work phone',  type: 'tel' },
    { key: 'website',        label: 'Website',     type: 'url' },
    { key: 'address',        label: 'Address',     type: 'text' },
    { key: 'notes',          label: 'Notes',       type: 'textarea' },
  ],
};

export default function PreviewScreen() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [fields, setFields] = useState(state?.extracted || {});
  const [saving, setSaving] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  if (!state) {
    navigate('/app', { replace: true });
    return null;
  }

  const { category } = state;
  const schema = FIELD_SCHEMAS[category] || [];
  const isContact = category === 'businesscard';
  const msConnected = Boolean(ms.getAccount());

  const handleChange = (key, value) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  const handleOutlook = async () => {
    setSaving(true);
    try {
      if (isContact) await addContact({ fields });
      else           await addToCalendar({ category, fields });
      setConfirmed(true);
      setTimeout(() => navigate('/app'), 1200);
    } catch (e) {
      console.error(e);
      alert(e.message || (isContact ? 'Failed to add contact.' : 'Failed to add to calendar.'));
      setSaving(false);
    }
  };

  const handleDownload = () => {
    try {
      if (isContact) downloadVcf({ fields });
      else           downloadIcs({ category, fields });
      setConfirmed(true);
      setTimeout(() => navigate('/app'), 1500);
    } catch (e) {
      console.error(e);
      alert(e.message || 'Failed to download.');
    }
  };

  return (
    <div>
      <button
        className="btn btn--ghost"
        onClick={() => navigate(-1)}
        style={{ marginBottom: 16 }}
      >
        <ArrowLeft size={16} /> Back
      </button>
      <h1 className="h1">Confirm details</h1>
      <p className="muted" style={{ marginBottom: 20 }}>
        Edit anything before saving.
      </p>

      <div className="card" style={{ marginBottom: 16 }}>
        {schema.map(({ key, label, type }) => (
          <div className="field" key={key}>
            <label className="field__label">{label}</label>
            {type === 'textarea' ? (
              <textarea
                className="field__input"
                rows={3}
                value={fields[key] ?? ''}
                onChange={(e) => handleChange(key, e.target.value)}
              />
            ) : (
              <input
                className="field__input"
                type={type}
                value={fields[key] ?? ''}
                onChange={(e) => handleChange(key, e.target.value)}
              />
            )}
          </div>
        ))}
      </div>

      {confirmed ? (
        <div className="btn btn--primary btn--block" style={{ pointerEvents: 'none' }}>
          <Check size={18} /> Done!
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {msConnected && (
            <button
              className="btn btn--primary btn--block"
              onClick={handleOutlook}
              disabled={saving}
            >
              {saving
                ? <><div className="spinner" /> Adding…</>
                : isContact
                ? <><UserPlus size={18} /> Save to Outlook contacts</>
                : <><Calendar size={18} /> Add to Outlook calendar</>}
            </button>
          )}
          <button
            className={msConnected ? 'btn btn--ghost btn--block' : 'btn btn--primary btn--block'}
            onClick={handleDownload}
            disabled={saving}
          >
            <Download size={18} />
            {isContact
              ? ' Download for Apple / Google contacts (.vcf)'
              : ' Download for Apple / Google calendar (.ics)'}
          </button>
        </div>
      )}
    </div>
  );
}
